import { useMutation } from '@apollo/client/react';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useAddEmail } from '../../../hooks/apolloHooks/useAddEmail';
import { useDeleteEmail } from '../../../hooks/apolloHooks/useDeleteEmail';
import { ADD_CUSTOMER_TASK_MUTATION } from '../../../integrations/allkort/graphql/gql/CustomerTasksMutations';
import { ContactDataResponse } from '../../../integrations/allkort/graphql/interface/Customer';
import { AddCustomerTaskInput, AddCustomerTaskResponse } from '../../../integrations/allkort/graphql/interface/CustomerTaskMutation';
import { validateEmail } from '../../../utils/contactValidationSchemas';
import { EMAIL_TYPES } from '../../../utils/filterEmailsByType';
import { ClockIcon, CloseIcon, FrontCarIcon, InfoIconOutlined, PenIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Link from '../Link';
import RightsWrapper from '../RightsWrapper';
import Tooltip from '../Tooltip';
import VerifyEmail from '../VerifyEmail';

type ProfileVehicleSpecificationProps = {
	customerContactData: ContactDataResponse
	customerId: number
	refetchContactData?: () => void
	wrapperClasses?: string;
}

type DraftEmail =
	| { kind: 'existing'; id: number; address: string; verified: boolean }
	| { kind: 'new'; address: string }

/**
 * Calculate the next vehicle specification date
 * Returns the 15th of current month if today is before the 15th,
 * otherwise returns the 15th of next month (with proper year handling for December)
 */
const getNextVehicleSpecificationDate = (): string => {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const currentDay = today.getDate();
	const currentMonth = today.getMonth();
	const currentYear = today.getFullYear();

	let targetDate: Date;

	if (currentDay < 15) {
		// Before 15th - use 15th of current month
		targetDate = new Date(currentYear, currentMonth, 15);
	} else {
		// 15th or after - use 15th of next month (Date constructor handles year rollover for December)
		targetDate = new Date(currentYear, currentMonth + 1, 15);
	}

	// Format with Swedish ordinal suffix for day
	const day = 15;
	const dayOrdinal = `${day}:e`;  // 15:e in Swedish
	const monthName = targetDate.toLocaleDateString('sv-SE', { month: 'long' });
	const year = targetDate.getFullYear();

	return `${dayOrdinal} ${monthName} ${year}`;
};

const ProfileVehicleSpecification: React.FC<ProfileVehicleSpecificationProps> = ({
	customerContactData,
	customerId,
	refetchContactData,
	wrapperClasses = ''
}) => {
	const [editMode, setEditMode] = useState(false);
	const [draftEmails, setDraftEmails] = useState<DraftEmail[]>([]);
	const [newEmailInput, setNewEmailInput] = useState('');
	const [newEmailError, setNewEmailError] = useState<string | undefined>(undefined);
	const [isNewEmailValid, setIsNewEmailValid] = useState(false);
	const [pendingVerification, setPendingVerification] = useState<{ email: string; emailId: number } | null>(null);
	const [isAddingEmail, setIsAddingEmail] = useState(false);
	const [isEmailVerifying, setIsEmailVerifying] = useState(false);

	const { addEmail } = useAddEmail();
	const { deleteEmail } = useDeleteEmail();
	const [addCustomerTask] = useMutation<AddCustomerTaskResponse, AddCustomerTaskInput>(ADD_CUSTOMER_TASK_MUTATION);

	const customerNumber = customerContactData?.currentCustomer?.customerNumber;

	const sendActivationTask = async () => {
		if (!customerId || !customerNumber) return;
		const dueDate = new Date(Date.now() + 60 * 60 * 1000).toISOString();
		await addCustomerTask({
			variables: {
				customerId: Number(customerId),
				description: `Kund har aktiverat fordonspecifikation\r\nKundnummer: ${customerNumber}`,
				dueDate,
			},
		}).catch((err: unknown) => console.error('[ProfileVehicleSpecification] sendActivationTask failed:', err));
	};

	const sendDeactivationTask = async () => {
		if (!customerId || !customerNumber) return;
		const dueDate = new Date(Date.now() + 60 * 60 * 1000).toISOString();
		await addCustomerTask({
			variables: {
				customerId: Number(customerId),
				description: `Kund har inaktiverat fordonspecifikation\r\nKundnummer: ${customerNumber}`,
				dueDate,
			},
		}).catch((err: unknown) => console.error('[ProfileVehicleSpecification] sendDeactivationTask failed:', err));
	};

	const vehicleSpecEmails = (customerContactData?.currentCustomer?.emails || [])
		.filter(e => e.type.id === EMAIL_TYPES.VEHICLE_SPECIFICATION);
	const isActive = vehicleSpecEmails.length > 0;

	const openEditMode = () => {
		setDraftEmails(vehicleSpecEmails.map(e => ({ kind: 'existing', id: e.id, address: e.address, verified: e.verified })));
		setNewEmailInput('');
		setNewEmailError(undefined);
		setIsNewEmailValid(false);
		setIsAddingEmail(false);
		setPendingVerification(null);
		setEditMode(true);
	};

	const cancelEdit = async () => {
		if (pendingVerification) {
			await deleteEmail({ emailId: pendingVerification.emailId });
			setPendingVerification(null);
		}
		const shouldRefetch = hasChanges;
		setDraftEmails([]);
		setNewEmailInput('');
		setNewEmailError(undefined);
		setIsNewEmailValid(false);
		setEditMode(false);
		if (shouldRefetch) {
			refetchContactData?.();
		}
	};

	const removeDraftEmail = (index: number) => {
		const email = draftEmails[index];
		if (pendingVerification && email.kind === 'existing' && email.id === pendingVerification.emailId) {
			setPendingVerification(null);
		}
		const remainingAfterRemoval = draftEmails.filter((_, i) => i !== index);
		const wasLastEmail = remainingAfterRemoval.filter(e => e.kind === 'existing').length === 0 && vehicleSpecEmails.length === 1;
		setDraftEmails(remainingAfterRemoval);
		if (email.kind === 'existing') {
			deleteEmail({ emailId: email.id })
				.then(() => { if (wasLastEmail) sendDeactivationTask(); })
				.catch(err => console.error('[ProfileVehicleSpecification] deleteEmail failed:', err));
		}
	};

	const handleNewEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setNewEmailInput(value);
		if (value.length === 0) {
			setNewEmailError(undefined);
			setIsNewEmailValid(false);
			return;
		}
		const result = validateEmail(value);
		setIsNewEmailValid(result.valid);
		setNewEmailError(result.valid ? undefined : result.error);
	};

	const handleAddEmail = async () => {
		const trimmed = newEmailInput.trim();
		const result = validateEmail(trimmed);
		if (!result.valid || draftEmails.some(e => e.address === trimmed) || !customerId) return;
		setIsAddingEmail(true);
		try {
			const added = await addEmail({ address: trimmed, customerId, emailType: 'VEHICLE_SPECIFICATION' });
			const emailId = added?.[0]?.id;
			if (emailId) {
				setPendingVerification({ email: trimmed, emailId });
				setNewEmailInput('');
				setNewEmailError(undefined);
				setIsNewEmailValid(false);
			}
		} catch (err) {
			console.error('[ProfileVehicleSpecification] addEmail failed:', err);
		} finally {
			setIsAddingEmail(false);
		}
	};

	const originalIds = new Set(vehicleSpecEmails.map(e => e.id));
	const remainingIds = new Set(
		draftEmails.filter((e): e is Extract<DraftEmail, { kind: 'existing' }> => e.kind === 'existing').map(e => e.id)
	);
	const hasChanges =
		draftEmails.some(e => e.kind === 'existing' && !originalIds.has(e.id)) ||
		[...originalIds].some(id => !remainingIds.has(id));

	// TODO: also when available we need to use future mutaion of setting vechicle specification active or not instead of just checking if there are any emails, this is because we want to allow users to have emails added but not active until they choose to activate it. This is for better user experience so they can add all emails and then activate instead of having to add email, activate, add email, activate etc.
	return (
		<div className={classNames(
			'profile-settings-vehicle-specification',
			'profilesettingsvehiclespecification_component',
			'white-pale-section',
			'shadow-one',
			'with-header',
			wrapperClasses
		)}>
			<div className="header">
				<h2 className="body-text">
					<FrontCarIcon width={26} height={26} />
					Fordonsspecifikation
					<Tooltip text="Detta är ett bra komplement för dig med flera kort och som vill ha koll på förbrukning per kort." position="top-end">
						<InfoIconOutlined width="26" height="26" />
					</Tooltip>
				</h2>
			</div>

			<div className="content">
				<div className="introduction">
					{!editMode ? (
						<>
							<p>Fordonspecifikation är en detaljerad vy med dina tankningar över månaden som skickas med din faktura.</p>
							<div className="status">
								{isActive ? (
									<span className="active">Aktiv</span>
								) : (
									<span className="inactive">Inaktiv</span>
								)}
							</div>
						</>
					) : (
						<>
							{!isActive ? (
								<p>
									Aktivera fordonsspecifikation genom att lägga till e-postadresser som ska få specifikationen.
									E-postadresserna behöver verifieras innan de kan ta del av specifikationen.
								</p>
							) : (
								<p>Hantera vilka som får fordonsspecifikation genom att lägga till eller ta bort e-postadresser nedan.</p>
							)}
						</>
					)}
				</div>

				{!editMode ? (
					<>
						{isActive ? (
							<div className="emails-with-specification handle-emails">
								<p className="body-text">
									{`${vehicleSpecEmails.length} ${vehicleSpecEmails.length === 1 ? 'e-postadress' : 'e-postadresser'} har fordonsspecifikation`}
								</p>
								<RightsWrapper>
									<Link
										href={'#'}
										className="edit-action"
										onClick={openEditMode}
									>
										<PenIcon width={18} height={18} />
										{'Hantera'}
									</Link>
								</RightsWrapper>
							</div>
						) : (
							<RightsWrapper>
								<button
									type="button"
									className="body-text button-edit"
									onClick={openEditMode}
								>
									Aktivera fordonsspecifikation
								</button>
							</RightsWrapper>
						)}
						{isActive && (
							<div className="specification-details">
								<h6 className="body-text">
									<ClockIcon width={22} height={22} />
									Kommande utskick
								</h6>
								<p>Nästa specifikation skickas den <strong>{getNextVehicleSpecificationDate()}.</strong></p>
							</div>
						)}

					</>
				) : (
					<>
						<div className="add-email">
							<div>
								<input
									type="email"
									placeholder="Lägg till e-postadress"
									name="newEmail"
									value={newEmailInput}
									onChange={handleNewEmailChange}
									onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
								/>
								<Button
									type="button"
									className="add-email-button body-text"
									disabled={!isNewEmailValid || !!pendingVerification || isAddingEmail}
									onClick={handleAddEmail}
								>
									Lägg till
								</Button>
							</div>
							{newEmailError && newEmailInput.length > 0 && (
								<span className="input-error">{newEmailError}</span>
							)}
						</div>

						{draftEmails.length > 0 && (
							<div className="vehicle-emails-list">
								{draftEmails.map((email, index) => (
									<div
										key={email.kind === 'existing' ? email.id : `new-${email.address}`}
										className={classNames('vehicle-email', {
											'vehicle-email--new': email.kind === 'new',
											'vehicle-email--unverified': email.kind === 'existing' && !email.verified,
										})}
									>
										<span
											className="vehicle-email__address"
											onClick={() => removeDraftEmail(index)}
										>
											{email.address}
											<CloseIcon width={24} height={24} />
										</span>
									</div>
								))}
							</div>
						)}

						{draftEmails.some(e => e.kind === 'existing' && !e.verified) && (
							<p className="input-error">Vissa e-postadresser är inte verifierade. De är markerade i rött, vänligen ta bort dem och lägg till igen för att gå igenom verifieringsprocessen.</p>
						)}

						{isAddingEmail && (
							<span className="loader-zoom"></span>
						)}

						{pendingVerification && (
							<VerifyEmail
								email={pendingVerification.email}
								emailId={pendingVerification.emailId}
								onVerifySuccess={() => setIsEmailVerifying(true)}
								onVerified={(verifiedEmail) => {
									const emailId = pendingVerification!.emailId;
									const wasFirstEmail = vehicleSpecEmails.length === 0;
									setDraftEmails(prev => [...prev, { kind: 'existing', id: emailId, address: verifiedEmail, verified: true }]);
									setPendingVerification(null);
									setIsEmailVerifying(false);
									if (wasFirstEmail) sendActivationTask();
								}}
							/>
						)}

						<div className="vehicle-emails-actions">
							<Button
								type="button"
								onClick={cancelEdit}
								className="button-cancel body-text"
								disabled={isEmailVerifying}
							>
								Tillbaka
							</Button>
						</div>
					</>
				)}

			</div>
		</div>
	);
}

export default ProfileVehicleSpecification;